import React from "react"

import type { AudioRegionReviewConfig, MetricRecord } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { MetricOutputDisplaySource } from "@/types/RunMetricOutput"

import { NonExpressivePausesConfig } from "../../configuration/components/NonExpressivePausesConfig"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"

const CATEGORY = "non_expressive_pause"

export class NonExpressivePausesMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  aggregate_units = "/min"

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    // Prefer top-level subvalues_by_timestamp; fallback to result_json
    const rawSubvalues = Array.isArray(mo?.subvalues_by_timestamp)
      ? (mo.subvalues_by_timestamp ?? [])
      : Array.isArray(
            (
              mo?.result_json as
                | { subvalues_by_timestamp?: unknown[] }
                | undefined
            )?.subvalues_by_timestamp,
          )
        ? ((
            mo.result_json as {
              subvalues_by_timestamp: typeof mo.subvalues_by_timestamp
            }
          ).subvalues_by_timestamp ?? [])
        : []
    if (rawSubvalues.length === 0) {
      return []
    }
    return rawSubvalues.map((item, idx) => ({
      id: `${CATEGORY}:${idx}`,
      start: item.start_offset,
      end: Math.max(item.end_offset, item.start_offset + 0.05),
      color: "rgba(255, 0, 0, 0.7)",
      drag: false,
      resize: false,
    }))
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "per_minute",
      buildReviewedResultJson: ({ editedCount, score, durationSeconds }) => ({
        non_expressive_pauses_count: editedCount,
        non_expressive_pauses_per_minute: Number(score.toFixed(2)),
        duration_seconds:
          durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
      }),
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
    editorContext?: Parameters<MetricRecord["renderEditorMetadata"]>[3],
  ): React.ReactNode {
    return (
      <div className="flex flex-col gap-4">
        <NonExpressivePausesConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(
          metricRecordData,
          onChange,
          textAreaRef,
          editorContext,
        )}
      </div>
    )
  }
}
